<script setup>
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  DateFormatter,
  getLocalTimeZone,
  parseDate,
  CalendarDate,
} from "@internationalized/date";
import {
  RangeCalendarRoot,
  RangeCalendarGrid,
  RangeCalendarGridHead,
  RangeCalendarGridBody,
  RangeCalendarGridRow,
  RangeCalendarHeadCell,
  RangeCalendarPrev,
  RangeCalendarNext,
  RangeCalendarCell,
  RangeCalendarCellTrigger,
} from "reka-ui";
import { Popover, PopoverTrigger, PopoverContent } from "@/shared/ui/popover";
import Button from "@/shared/ui/button/Button.vue";
import { cn } from "@/shared/lib/utils";
import { buttonVariants } from "@/shared/ui/button";

const props = defineProps({
  startDate: {
    type: String,
    default: "",
  },
  endDate: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "Выберите период",
  },
  triggerClass: {
    type: [String, Object, Array],
    default: "",
  },
  yearRange: {
    type: Array,
    default: () => {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let y = currentYear; y >= 1900; y--) {
        years.push(y);
      }
      return years;
    },
  },
});

const emit = defineEmits(["update:startDate", "update:endDate"]);

const { t } = useI18n();

const df = new DateFormatter("ru-RU", { dateStyle: "short" });

const months = computed(() => [
  t("Январь"),
  t("Февраль"),
  t("Март"),
  t("Апрель"),
  t("Май"),
  t("Июнь"),
  t("Июль"),
  t("Август"),
  t("Сентябрь"),
  t("Октябрь"),
  t("Ноябрь"),
  t("Декабрь"),
]);

const calendarPlaceholder = ref(
  new CalendarDate(new Date().getFullYear(), new Date().getMonth() + 1, 1)
);

const isOpen = ref(false);

function safeParse(dateStr) {
  if (!dateStr) return undefined;
  try {
    return parseDate(dateStr);
  } catch {
    return undefined;
  }
}

// Internal pending range (not emitted until "Применить")
const pendingRange = ref({
  start: safeParse(props.startDate),
  end: safeParse(props.endDate),
});

// Sync props → pending when popover opens
watch(isOpen, (open) => {
  if (open) {
    pendingRange.value = {
      start: safeParse(props.startDate),
      end: safeParse(props.endDate),
    };
  }
});

const displayValue = computed(() => {
  const start = safeParse(props.startDate);
  const end = safeParse(props.endDate);
  if (!start && !end) return "";
  const startStr = start ? df.format(start.toDate(getLocalTimeZone())) : "...";
  const endStr = end ? df.format(end.toDate(getLocalTimeZone())) : "...";
  return `${startStr} — ${endStr}`;
});

const canApply = computed(() => {
  return pendingRange.value?.start && pendingRange.value?.end;
});

function apply() {
  const val = pendingRange.value;
  emit("update:startDate", val?.start ? val.start.toString() : "");
  emit("update:endDate", val?.end ? val.end.toString() : "");
  isOpen.value = false;
}

function clear() {
  emit("update:startDate", "");
  emit("update:endDate", "");
  pendingRange.value = { start: undefined, end: undefined };
  isOpen.value = false;
}

function onMonthChange(e) {
  const month = Number(e.target.value);
  calendarPlaceholder.value = calendarPlaceholder.value.set({ month });
}

function onYearChange(e) {
  const year = Number(e.target.value);
  calendarPlaceholder.value = calendarPlaceholder.value.set({ year });
}

watch(
  () => props.startDate,
  (val) => {
    const parsed = safeParse(val);
    if (parsed) {
      calendarPlaceholder.value = parsed.copy();
    }
  }
);
</script>

<template>
  <div class="date-range-picker">
    <Popover v-model:open="isOpen">
      <PopoverTrigger as-child>
        <Button
          variant="outline"
          :class="
            cn(
              'w-full justify-start text-left font-normal h-8 text-xs px-2',
              !displayValue && 'text-muted-foreground',
              props.triggerClass
            )
          "
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="mr-1.5 h-3.5 w-3.5 shrink-0"
          >
            <path d="M8 2v4" />
            <path d="M16 2v4" />
            <rect width="18" height="18" x="3" y="4" rx="2" />
            <path d="M3 10h18" />
          </svg>
          <span v-if="displayValue" class="truncate">{{ displayValue }}</span>
          <span v-else class="text-muted-foreground">{{
            $t(props.placeholder)
          }}</span>
          <svg
            v-if="displayValue"
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground hover:text-foreground"
            @click.stop="clear"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </Button>
      </PopoverTrigger>
      <PopoverContent class="w-auto p-0" align="start">
        <RangeCalendarRoot
          v-slot="{ grid, weekDays }"
          v-model="pendingRange"
          v-model:placeholder="calendarPlaceholder"
          locale="ru"
          class="p-3"
          initial-focus
        >
          <div class="flex items-center justify-between gap-2 pb-2">
            <RangeCalendarPrev
              :class="
                cn(
                  buttonVariants({ variant: 'outline' }),
                  'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
                )
              "
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="h-4 w-4"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
            </RangeCalendarPrev>

            <div class="flex items-center gap-1">
              <select
                :value="calendarPlaceholder.month"
                class="appearance-none bg-transparent text-sm font-medium cursor-pointer hover:bg-accent rounded px-1.5 py-0.5 outline-none"
                @change="onMonthChange"
              >
                <option v-for="(m, i) in months" :key="i" :value="i + 1">
                  {{ m }}
                </option>
              </select>

              <select
                :value="calendarPlaceholder.year"
                class="appearance-none bg-transparent text-sm font-medium cursor-pointer hover:bg-accent rounded px-1.5 py-0.5 outline-none"
                @change="onYearChange"
              >
                <option v-for="y in yearRange" :key="y" :value="y">
                  {{ y }}
                </option>
              </select>
            </div>

            <RangeCalendarNext
              :class="
                cn(
                  buttonVariants({ variant: 'outline' }),
                  'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
                )
              "
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="h-4 w-4"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
            </RangeCalendarNext>
          </div>

          <div
            class="flex flex-col gap-y-4 mt-2 sm:flex-row sm:gap-x-4 sm:gap-y-0"
          >
            <RangeCalendarGrid
              v-for="month in grid"
              :key="month.value.toString()"
              class="w-full border-collapse space-y-1"
            >
              <RangeCalendarGridHead>
                <RangeCalendarGridRow class="flex">
                  <RangeCalendarHeadCell
                    v-for="day in weekDays"
                    :key="day"
                    class="w-9 rounded-md text-[0.8rem] font-normal text-muted-foreground"
                  >
                    {{ day }}
                  </RangeCalendarHeadCell>
                </RangeCalendarGridRow>
              </RangeCalendarGridHead>
              <RangeCalendarGridBody>
                <RangeCalendarGridRow
                  v-for="(weekDates, index) in month.rows"
                  :key="`weekDate-${index}`"
                  class="flex mt-2 w-full"
                >
                  <RangeCalendarCell
                    v-for="weekDate in weekDates"
                    :key="weekDate.toString()"
                    :date="weekDate"
                    class="relative h-9 w-9 p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([data-selected])]:bg-accent [&:has([data-selected][data-outside-view])]:bg-accent/50"
                  >
                    <RangeCalendarCellTrigger
                      :day="weekDate"
                      :month="month.value"
                      :class="
                        cn(
                          buttonVariants({ variant: 'ghost' }),
                          'h-9 w-9 p-0 font-normal',
                          '[&[data-today]:not([data-selected])]:bg-accent [&[data-today]:not([data-selected])]:text-accent-foreground',
                          'data-[selected]:bg-primary data-[selected]:text-primary-foreground data-[selected]:opacity-100',
                          'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
                          'data-[selection-start]:bg-primary data-[selection-start]:text-primary-foreground data-[selection-start]:rounded-l-md',
                          'data-[selection-end]:bg-primary data-[selection-end]:text-primary-foreground data-[selection-end]:rounded-r-md',
                          'data-[disabled]:text-muted-foreground data-[disabled]:opacity-50',
                          'data-[unavailable]:text-destructive-foreground data-[unavailable]:line-through',
                          'data-[outside-view]:text-muted-foreground data-[outside-view]:opacity-50'
                        )
                      "
                    />
                  </RangeCalendarCell>
                </RangeCalendarGridRow>
              </RangeCalendarGridBody>
            </RangeCalendarGrid>
          </div>
        </RangeCalendarRoot>
        <div class="flex items-center justify-between gap-2 p-3 pt-0">
          <Button variant="ghost" size="sm" @click="clear">
            {{ $t("Очистить") }}
          </Button>
          <Button size="sm" :disabled="!canApply" @click="apply">
            {{ $t("Применить") }}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  </div>
</template>
